import { isNull } from 'lodash';
import React from 'react';
import { Options } from 'react-select';

import { FieldValue } from '../../../datasets/instances/store';
import { SingleDatasetFieldType } from '../../../datasets/store';
import { IconButton } from '../../../shared/button/IconButton';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Calendar, Delete } from '../../../shared/icons';
import { LongText } from '../../../shared/longtext/LongText';
import { Text } from '../../../shared/text/Text';
import { Toggle } from '../../../shared/toggle';
import { OverflowTooltip } from '../../../shared/tooltip';
import { InformationTooltip } from '../../../shared/tooltip/InformationTooltip';
import styles from './AIManager.module.scss';
import { DocumentQuery } from './store';

const { red } = styles;

interface AgreementTypeSystemIdOptions {
    value: string;
    label: string;
}

interface RelevantDocumentNames {
    documentName: string;
    documentNameId: number | undefined;
}

interface AIQueryItemProps {
    query: DocumentQuery;
    isEditing: boolean;
    agreementTypeSystemIdOptions: AgreementTypeSystemIdOptions[];
    relevantDocumentNames: RelevantDocumentNames[];
    testId: string;
    updateDisplayQuestion: (queryOrder: number, value: string | null) => void;
    updateDocumentNameIds: (queryOrder: number, documentNameId: number, currentIds: number[]) => void;
    queryKeyAlreadyExists: (queryOrder: number, queryKey: string) => boolean;
    updateQuery: (queryOrder: number, key: keyof DocumentQuery, value: string | number | number[] | FieldValue) => void;
    updateSystemId: (dropdownValue: DropdownOption | Options<DropdownOption> | null, queryOrder: number) => void;
    handleFetchDocumentQueryHistory?: (documentQueryId: number) => void;
    deleteNewQuery?: (queryOrder: number) => void;
    getDefaultValueElement: (
        queryOrder: number,
        defaultAnswer: FieldValue,
        fieldType: SingleDatasetFieldType | null,
        dropdownListId: number | null,
        isMultiSelect: number,
        isEdgeCase: number
    ) => React.ReactNode;
}

export const AIQueryItem: React.FC<AIQueryItemProps> = ({
    query,
    isEditing,
    agreementTypeSystemIdOptions,
    relevantDocumentNames,
    updateQuery,
    updateDisplayQuestion,
    updateSystemId,
    getDefaultValueElement,
    updateDocumentNameIds,
    handleFetchDocumentQueryHistory,
    deleteNewQuery,
    queryKeyAlreadyExists,
    testId = 'ai-query'
}) => {
    const {
        documentQueryId,
        queryKey,
        systemId,
        correctQueries,
        documentNameIds,
        question,
        displayQuestion,
        totalQueries,
        isEdgeCase,
        multiRowQuery,
        partyRowExists,
        queryOrder,
        guidanceNotes,
        fieldType,
        dropdownListId,
        isMultiSelect,
        defaultAnswer,
        percentage,
        originalQueryId
    } = query;

    const existingDisabled = !isEditing || (isEditing && !isNull(documentQueryId));
    const queryKeyBorder = isNull(documentQueryId) && queryKey.length > 0 && queryKeyAlreadyExists(queryOrder, queryKey) ? red : undefined;
    const showEdgeCaseTooltip = isNull(documentQueryId) && isEditing;
    const systemIdValue = { label: systemId, value: systemId };

    return (
        <div className={styles.aiQueryWrapperWithOrder} key={documentQueryId} id={`ai-query-${queryOrder}-wrapper`}>
            <div className={styles.aiQueryWrapper} key={documentQueryId} id={`ai-query-${queryOrder}`}>
                <div className={styles.aiDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Question</div>
                    <LongText testId={`${testId}-question`} disabled={!isEditing} value={question} marginBottom='0px' onChange={value => updateQuery(queryOrder, 'question', value)} width='70%' maxLength={1000} />
                </div>
                <div className={styles.aiDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Display Question</div>
                    <LongText testId={`${testId}-display-question`} disabled={!isEditing} value={displayQuestion || ''} marginBottom='0px' onChange={value => updateDisplayQuestion(queryOrder, value)} width='70%' height='60px' maxLength={500} />
                </div>
                <div className={styles.aiDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Guidance Notes</div>
                    <LongText testId={`${testId}-guidance-notes`} disabled={!isEditing} value={guidanceNotes} marginBottom='0px' onChange={value => updateQuery(queryOrder, 'guidanceNotes', value)} width='70%' maxLength={1000} />
                </div>
                <div className={styles.aiDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>Key</div>
                    <Text testId={`${testId}-key`} borderColour={queryKeyBorder} disabled={existingDisabled} value={queryKey} onChange={e => updateQuery(queryOrder, 'queryKey', e.target.value)} marginBottom='0px' width='70%' />
                </div>
                <div className={styles.aiDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>System ID</div>
                    <div style={{ width: '70%' }}>
                        <Dropdown
                            value={systemIdValue}
                            options={agreementTypeSystemIdOptions}
                            onChange={e => updateSystemId(e, queryOrder)}
                            testId={`${testId}-system-id`}
                            disabled={existingDisabled}
                        />
                    </div>
                </div>
                {getDefaultValueElement(queryOrder, defaultAnswer, fieldType, dropdownListId, isMultiSelect, isEdgeCase)}
                <div className={styles.aiDataPointWrapper}>
                    <div className={styles.aiDataPointLabel}>
                        Edge Case?
                        {showEdgeCaseTooltip &&
                            <div className={styles.edgeCaseTooltip}>
                                <InformationTooltip content='Any question that relates to more than one field is considered an edge case.' />
                            </div>
                        }
                    </div>
                    <Toggle testId={`${testId}-edge-case`} disabled={existingDisabled} checked={[isEdgeCase, multiRowQuery, partyRowExists].includes(1)} onChange={value => updateQuery(queryOrder, 'isEdgeCase', value ? 1 : 0)} />
                </div>
                <div className={styles.documentNamesWrapper}>
                    {relevantDocumentNames.map(({ documentNameId, documentName }) => (
                        <div className={styles.aiDataPointWrapper} key={documentNameId}>
                            <div className={styles.aiDataPointLabel}>
                                <OverflowTooltip overlayText={documentName} />
                            </div>
                            <Toggle testId={`${testId}-document-name-${documentName}`} disabled={!isEditing} checked={documentNameIds.includes(documentNameId!)} onChange={() => updateDocumentNameIds(queryOrder, documentNameId!, documentNameIds)} />
                        </div>
                    ))}
                </div>
                <div className={styles.historicalQueryWrapper}>
                    {documentQueryId && !isNull(originalQueryId) && handleFetchDocumentQueryHistory &&
                        <div style={{ marginRight: '5px' }}>
                            <IconButton overlayText='Historical Queries' onClick={() => handleFetchDocumentQueryHistory(documentQueryId)} icon={Calendar} disabled={isEditing} />
                        </div>}
                    {documentQueryId && isEdgeCase === 0 &&
                        <div className={styles.percentageWrapper}>
                            <div className={styles.percentageBar}>
                                <div className={styles.successPercentage} style={{ width: `${percentage}%` }} />
                            </div>
                            <div className={styles.successInfo}>
                                <div className={styles.successString}>
                                    <OverflowTooltip overlayText={`Success Rate: ${correctQueries} / ${totalQueries}`} />
                                </div>
                                <div className={styles.displayPercentage}>{percentage}%</div>
                            </div>
                        </div>
                    }
                </div>
                {isNull(documentQueryId) && isEditing && deleteNewQuery &&
                    <div className={styles.deleteQuery}>
                        <IconButton icon={Delete} onClick={() => deleteNewQuery(queryOrder)} color={red} />
                    </div>}
            </div>
        </div>
    );
};
